import React, { useEffect, useRef } from "react";
import DOMPurify from "dompurify";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import PanelDecorText from "../../img/decor/panel_decor_text.webp";


const PanelItemsGradient = [
    'linear-gradient(90deg, #CDEDF0 0%, #F0BBD8 100%)',
    'linear-gradient(90deg, #FFF2AA 0%, #D4F565 100%)',
    'linear-gradient(90deg, #D1BBF1 0%, #BEE8E9 100%)',
    'linear-gradient(90deg, #FFEC85 0%, #FFAAA4 100%)'
]

gsap.registerPlugin(ScrollTrigger);

const PanelCardContent = ({ panel, index }) => (
    <>
        <div className="flex-1 flex flex-col justify-center max-lg:order-2">
            <div className="flex items-center gap-3 mb-6 max-lg:w-[calc(100%-60px)]">
                <h3 className="font-bold text-2xl lg:text-3xl ">
                    {panel.title}
                </h3>
            </div>
            <div
                className="text-sm lg:text-base leading-relaxed text-gray-800 prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(panel.text),
                }}
            />
        </div>

        <div className="max-lg:order-1">
            <img src={panel.img} alt={`Panel ${index + 1}`} className="w-full block rounded-[28px]" />
        </div>

        <div className="flex justify-center items-center lg:px-8 px-4 lg:h-24 h-16 absolute right-8 top-0 bg-white rounded-b-[8px]">
            <div className="font-bold text-[42px] text-black">
                {index + 1}
            </div>
        </div>
    </>
);

const EventPanelBase = ({ panelData }) => {
    const panel_list = panelData && (Array.isArray(panelData) ? panelData : panelData.panel_list);
    const panel_title = panelData?.panel_title || "ТЕМИ";
    const cardsRef = useRef([]);

    useEffect(() => {
        if (!panel_list || panel_list.length === 0) return;

        const cards = cardsRef.current.filter(Boolean);
        if (cards.length === 0) return;

        const triggers = [];

        cards.forEach((card, index) => {
            const isLastCard = index === cards.length - 1;
            const shrinkPercent = 9 + (cards.length - 1 - index) * 3;

            const animation = gsap.fromTo(
                card,
                {
                    scale: 1,
                    width: "100%",
                },
                {
                    scale: 0.95,
                    width: `${100 - shrinkPercent}%`,
                    duration: 0.3,
                    paused: true,
                    ease: "none",
                }
            );

            const trigger = ScrollTrigger.create({
                trigger: card,
                start: isLastCard ? "top 80%" : "top 96px",
                end: "+=50%",
                scrub: true,
                onUpdate: (self) => {
                    animation.progress(self.progress);
                    if (self.progress > 0.5) {
                        card.classList.add("panel-active");
                    } else {
                        card.classList.remove("panel-active");
                    }
                },
            });

            triggers.push(trigger);
        });

        return () => {
            triggers.forEach((trigger) => trigger.kill());
        };
    }, [panel_list]);

    //console.log("panelData:", panelData);

    if (!panelData || !panel_list || !Array.isArray(panel_list) || panel_list.length === 0) {
        return null;
    }

    return (
        <section className="relative my-[60px] lg:my-[150px]" id="panel">

            <img src={PanelDecorText} className="absolute w-full bottom-0 hidden md:block" alt="" />

            <div className="container max-w-screen-xl mx-auto px-5">
                <h2 className="text-[30px] uppercase text-center font-bold xl:text-[55px] mb-10 lg:mb-20 relative z-10">
                    {panel_title}
                </h2>

                <div className="flex flex-col lg:hidden">
                    {panel_list.map((panel, index) => {
                        const isLast = index === panel_list.length - 1;

                        return (
                            <div
                                key={`mobile-${index}`}
                                className="relative w-full mx-auto rounded-2xl overflow-hidden shadow-2xl grid grid-cols-1 gap-8 items-center py-8 px-6"
                                style={{
                                    marginBottom: isLast ? 0 : "40px",
                                    background: PanelItemsGradient[index % PanelItemsGradient.length],
                                }}
                            >
                                <PanelCardContent panel={panel} index={index} />
                            </div>
                        );
                    })}
                </div>

                <div className="hidden lg:flex flex-col">
                    {panel_list.map((panel, index) => {
                        const isLast = index === panel_list.length - 1;

                        return (
                            <div
                                key={`desktop-${index}`}
                                ref={(el) => (cardsRef.current[index] = el)}
                                className="w-full mx-auto rounded-2xl overflow-hidden shadow-2xl grid grid-cols-2 gap-8 items-center min-h-[500px] py-8 px-14 sticky"
                                style={{
                                    top: `${80 + index * 28}px`,
                                    zIndex: index + 1,
                                    transformOrigin: "center top",
                                    marginBottom: isLast ? 0 : "80px",
                                    background: PanelItemsGradient[index % PanelItemsGradient.length],
                                }}
                            >
                                <PanelCardContent panel={panel} index={index} />
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default EventPanelBase;